import {
  buscarMedicamentos,
  type CampoCatalogo,
  type EntradaDeMedicamento,
  etiquetaDeMedicamento,
  type Respuesta,
} from '@pasos/schemas';
import { useId, useState } from 'react';
import { Opcion } from '../../components/domain/Opcion.js';
import { PACIENTE } from '../../copy/index.js';

type CatalogoProps = {
  campo: CampoCatalogo;
  valor: string | undefined;
  onElegir: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
  /** La lista descargada. Vacía si todavía no llegó. */
  entradas: readonly EntradaDeMedicamento[];
};

/**
 * Una entrada de un catálogo de datos (E3.25): la droga, de la lista de
 * Fundación.
 *
 * **El buscador filtra, no escribe.** Lo tipeado achica la lista y no viaja a
 * ningún lado: la respuesta es siempre el código de una entrada, elegida con el
 * mismo radio grande de cualquier lista (`Opcion`). Si la droga no está, la
 * última opción es "no está en la lista", que queda registrada así, sin texto, y
 * el equipo la ve para que Fundación amplíe el catálogo. Nunca hay un campo
 * para escribir el nombre a mano.
 *
 * La lista es corta (unos veinte principios activos), así que se muestra entera
 * y el buscador es una ayuda, no una condición para ver opciones. Lo elegido
 * sigue a la vista aunque la búsqueda lo deje afuera.
 */
export function Catalogo({ campo, valor, onElegir, etiquetadoPor, entradas }: CatalogoProps) {
  const idBuscador = useId();
  const grupo = useId();
  const [busqueda, setBusqueda] = useState('');

  const encontradas = buscarMedicamentos(entradas, busqueda);
  const elegida = entradas.find((entrada) => entrada.codigo === valor);
  const visibles =
    elegida && !encontradas.some((entrada) => entrada.codigo === elegida.codigo)
      ? [elegida, ...encontradas]
      : encontradas;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-lg font-bold text-heading" htmlFor={idBuscador}>
          {PACIENTE.formulario.buscar}
        </label>
        <div className="flex min-h-15 items-center rounded-lg bg-card px-4 outline-2 -outline-offset-2 outline-input focus-within:shadow-halo-navy-100 focus-within:outline-ring">
          <input
            aria-describedby={`${idBuscador}-ayuda`}
            autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-transparent text-touch text-heading outline-none focus-visible:outline-none"
            id={idBuscador}
            onChange={(evento) => setBusqueda(evento.target.value)}
            type="search"
            value={busqueda}
          />
        </div>
        <p className="m-0 text-base text-subtle" id={`${idBuscador}-ayuda`}>
          {entradas.length === 0
            ? PACIENTE.formulario.listaSinDescargar
            : PACIENTE.formulario.buscarAyuda}
        </p>
      </div>

      <fieldset aria-labelledby={etiquetadoPor} className="m-0 flex flex-col gap-3 border-0 p-0">
        {visibles.map((entrada) => (
          <Opcion
            elegida={valor === entrada.codigo}
            key={entrada.codigo}
            nombre={grupo}
            onElegir={() => onElegir(entrada.codigo)}
            titulo={entrada.principioActivo}
            detalle={`${entrada.forma} ${entrada.concentracion}`}
            valor={entrada.codigo}
          />
        ))}
        {visibles.length === 0 && entradas.length > 0 ? (
          <p className="m-0 text-lg text-subtle" role="status">
            {PACIENTE.formulario.sinResultados}
          </p>
        ) : null}
        <Opcion
          elegida={valor === campo.fueraDeLista.codigo}
          nombre={grupo}
          onElegir={() => onElegir(campo.fueraDeLista.codigo)}
          titulo={campo.fueraDeLista.etiqueta}
          valor={campo.fueraDeLista.codigo}
        />
      </fieldset>
    </div>
  );
}

/** El nombre de lo elegido, para el eco: la entrada o la salida de fuera de lista. */
export function nombreDeLoElegido(
  campo: CampoCatalogo,
  valor: string,
  entradas: readonly EntradaDeMedicamento[],
): string {
  if (valor === campo.fueraDeLista.codigo) return campo.fueraDeLista.etiqueta;
  const entrada = entradas.find((candidata) => candidata.codigo === valor);
  return entrada ? etiquetaDeMedicamento(entrada) : valor;
}
